import { registerRoute } from '../router';
import { apiFetch, ApiError } from '../api';
import { getUser } from '../state';
import { escapeHtml } from '../format';
import { openModal } from '../components/modal';
import { extractVariables, renderTemplate } from '../../shared/templating';
import { CHANNELS, type Channel } from '../../shared/channels';

interface Template {
  id: string;
  name: string;
  channel: Channel;
  body: string;
  contentSid?: string;
  createdAt: string;
}

const CHANNEL_LABELS: Record<Channel, string> = {
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  email: 'Email',
  webhook: 'Webhook',
};

let templates: Template[] = [];

function canManage(): boolean {
  const role = getUser()?.role;
  return role === 'administrator' || role === 'manager';
}

async function loadTemplates(container: HTMLElement): Promise<void> {
  templates = await apiFetch<Template[]>('/api/templates');
  renderList(container);
}

function renderList(container: HTMLElement): void {
  const listEl = container.querySelector('#templates-list')!;
  const manage = canManage();

  if (templates.length === 0) {
    listEl.innerHTML = `<p style="color:var(--clr-text-muted);">No templates yet.</p>`;
    return;
  }

  listEl.innerHTML = templates
    .map((t) => {
      const variables = extractVariables(t.body);
      return `
      <div class="card" style="margin-bottom:12px;">
        <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:16px;">
          <div style="min-width:0;">
            <div style="font-weight:600;">${escapeHtml(t.name)}</div>
            <span class="badge badge--muted" style="margin-top:4px;">${CHANNEL_LABELS[t.channel]}</span>
            <p style="color:var(--clr-text-dim);font-size:0.85rem;margin-top:8px;white-space:pre-wrap;">${escapeHtml(t.body)}</p>
            ${variables.length > 0 ? `<div style="margin-top:8px;display:flex;gap:6px;flex-wrap:wrap;">${variables.map((v) => `<span class="badge badge--orange">{{${escapeHtml(v)}}}</span>`).join('')}</div>` : ''}
            ${t.contentSid ? `<div style="margin-top:6px;font-size:0.78rem;color:var(--clr-text-muted);">Content SID: <code>${escapeHtml(t.contentSid)}</code></div>` : ''}
          </div>
          ${
            manage
              ? `<div style="display:flex;gap:8px;flex-shrink:0;">
                   <button class="btn btn--ghost" data-action="edit" data-id="${t.id}">Edit</button>
                   <button class="btn btn--ghost" data-action="delete" data-id="${t.id}">Delete</button>
                 </div>`
              : ''
          }
        </div>
      </div>`;
    })
    .join('');

  if (!manage) return;

  listEl.querySelectorAll<HTMLButtonElement>('[data-action="edit"]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const template = templates.find((t) => t.id === btn.dataset.id);
      if (template) openTemplateModal(container, template);
    });
  });

  listEl.querySelectorAll<HTMLButtonElement>('[data-action="delete"]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      if (!confirm('Delete this template?')) return;
      await apiFetch(`/api/templates/${btn.dataset.id}`, { method: 'DELETE' });
      await loadTemplates(container);
    });
  });
}

function renderPreviewInputs(container: HTMLElement, variables: string[]): void {
  const previewFields = container.querySelector('#preview-fields')!;
  previewFields.innerHTML = variables
    .map(
      (v) => `
    <div class="field" style="margin-bottom:10px;">
      <label for="preview-${v}">{{${escapeHtml(v)}}}</label>
      <input class="input" id="preview-${v}" data-var="${v}" placeholder="Sample value">
    </div>`,
    )
    .join('');

  previewFields.querySelectorAll<HTMLInputElement>('[data-var]').forEach((input) => {
    input.addEventListener('input', () => updatePreview(container));
  });
}

function updatePreview(container: HTMLElement): void {
  const body = (container.querySelector('#template-body') as HTMLTextAreaElement).value;
  const values: Record<string, string> = {};
  container.querySelectorAll<HTMLInputElement>('#preview-fields [data-var]').forEach((input) => {
    if (input.value.trim()) values[input.dataset.var!] = input.value.trim();
  });
  container.querySelector('#preview-output')!.textContent = renderTemplate(body, values) || '(empty)';
}

function openTemplateModal(container: HTMLElement, existing?: Template): void {
  const isEdit = Boolean(existing);
  const channelOptions = CHANNELS.map(
    (c) => `<option value="${c}" ${existing?.channel === c ? 'selected' : ''}>${CHANNEL_LABELS[c]}</option>`,
  ).join('');

  const { root, close } = openModal(
    isEdit ? 'Edit template' : 'Add template',
    `
    <form id="template-form">
      <div class="field">
        <label for="template-name">Name</label>
        <input class="input" id="template-name" required value="${existing ? escapeHtml(existing.name) : ''}">
      </div>
      <div class="field">
        <label for="template-channel">Channel</label>
        <select class="input" id="template-channel">${channelOptions}</select>
      </div>
      <div class="field">
        <label for="template-body">Body</label>
        <textarea class="input" id="template-body" required placeholder="Hi {{name}}, ...">${existing ? escapeHtml(existing.body) : ''}</textarea>
      </div>
      <div id="content-sid-wrapper" class="field" style="${(existing?.channel ?? 'sms') === 'whatsapp' ? '' : 'display:none;'}">
        <label for="template-content-sid">Content Template SID <span style="font-weight:400;color:var(--clr-text-muted);">(WhatsApp — HXxxx…)</span></label>
        <input class="input" id="template-content-sid" placeholder="HXxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" value="${existing?.contentSid ? escapeHtml(existing.contentSid) : ''}">
        <p style="font-size:0.78rem;color:var(--clr-text-muted);margin-top:4px;">Required for business-initiated WhatsApp messages. Variables in the body map to {{1}}, {{2}}, … in the Twilio template in order of appearance.</p>
      </div>
      <div style="border-top:1px solid var(--clr-border);padding-top:14px;margin-bottom:14px;">
        <label style="font-size:0.8rem;font-weight:600;color:var(--clr-text-muted);">Preview</label>
        <div id="preview-fields" style="margin-top:10px;"></div>
        <p style="font-size:0.85rem;background:var(--clr-bg2);border-radius:var(--radius);padding:10px;white-space:pre-wrap;" id="preview-output"></p>
      </div>
      <p class="error-text" id="template-error"></p>
      <div style="display:flex;gap:10px;justify-content:flex-end;">
        <button type="button" class="btn btn--secondary" id="template-cancel">Cancel</button>
        <button type="submit" class="btn btn--primary">${isEdit ? 'Save changes' : 'Add template'}</button>
      </div>
    </form>
  `,
  );

  const bodyField = root.querySelector<HTMLTextAreaElement>('#template-body')!;
  const channelSelect = root.querySelector<HTMLSelectElement>('#template-channel')!;
  const contentSidWrapper = root.querySelector<HTMLElement>('#content-sid-wrapper')!;
  const contentSidInput = root.querySelector<HTMLInputElement>('#template-content-sid')!;

  channelSelect.addEventListener('change', () => {
    contentSidWrapper.style.display = channelSelect.value === 'whatsapp' ? '' : 'none';
    if (channelSelect.value !== 'whatsapp') contentSidInput.value = '';
  });

  function refreshVariables(): void {
    renderPreviewInputs(root, extractVariables(bodyField.value));
    updatePreview(root);
  }

  bodyField.addEventListener('input', refreshVariables);
  refreshVariables();

  root.querySelector('#template-cancel')?.addEventListener('click', close);

  root.querySelector('#template-form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const errorEl = root.querySelector('#template-error')!;
    errorEl.textContent = '';

    const name = (root.querySelector('#template-name') as HTMLInputElement).value.trim();
    const channel = channelSelect.value as Channel;
    const body = bodyField.value.trim();
    const contentSid = contentSidInput.value.trim() || null;

    try {
      if (isEdit && existing) {
        await apiFetch(`/api/templates/${existing.id}`, { method: 'PATCH', body: JSON.stringify({ name, channel, body, contentSid }) });
      } else {
        await apiFetch('/api/templates', { method: 'POST', body: JSON.stringify({ name, channel, body, ...(contentSid ? { contentSid } : {}) }) });
      }
      close();
      await loadTemplates(container);
    } catch (err) {
      errorEl.textContent = err instanceof ApiError ? err.message : 'Something went wrong.';
    }
  });
}

registerRoute({
  path: '/templates',
  title: 'Templates',
  render: ({ container }) => {
    const manage = canManage();

    container.innerHTML = `
      <div class="page-header" style="display:flex;align-items:flex-start;justify-content:space-between;gap:16px;">
        <div>
          <h1>Templates</h1>
          <p>Reusable message bodies with <code>{{variables}}</code> filled in at send time.</p>
        </div>
        ${manage ? '<button class="btn btn--primary" id="add-template-btn">+ Add template</button>' : ''}
      </div>
      <div id="templates-list">Loading…</div>
    `;

    container.querySelector('#add-template-btn')?.addEventListener('click', () => openTemplateModal(container));

    loadTemplates(container).catch(() => {
      container.querySelector('#templates-list')!.textContent = 'Could not load templates.';
    });
  },
});
